import { useCallback, useMemo } from "react"
import { useSearchParams } from "react-router-dom"

import type { PerformanceGrain } from "../types"
import type { ComparisonQuery } from "./types"

export type ComparisonMetric =
  | "avg_delay_days"
  | "on_time_percent"
  | "gap_amount"
  | "overrun_amount"
  | "margin_variance_points"
  | "schedule_score"
  | "financial_score"
  | "projects"

const GRAINS: PerformanceGrain[] = ["month", "quarter", "year"]
const METRICS: ComparisonMetric[] = [
  "avg_delay_days",
  "on_time_percent",
  "gap_amount",
  "overrun_amount",
  "margin_variance_points",
  "schedule_score",
  "financial_score",
  "projects"
]

export interface ComparisonPatch {
  grain?: PerformanceGrain
  to?: string | null
  memberIds?: string[]
  metric?: ComparisonMetric
}

// The comparison keeps its own `cmp_` keys so the dashboard's filters and this
// tab's never overwrite each other in the shared Team Management URL.
export function useComparisonUrlState() {
  const [params, setParams] = useSearchParams()
  const rawGrain = params.get("cmp_grain")
  const rawMetric = params.get("cmp_metric")

  const query: ComparisonQuery = useMemo(
    () => ({
      grain: GRAINS.includes(rawGrain as PerformanceGrain)
        ? (rawGrain as PerformanceGrain)
        : "month",
      to: params.get("cmp_to") ?? undefined,
      memberIds: (params.get("cmp_members") ?? "").split(",").filter(Boolean)
    }),
    [params, rawGrain]
  )
  const metric = METRICS.includes(rawMetric as ComparisonMetric)
    ? (rawMetric as ComparisonMetric)
    : "avg_delay_days"

  const update = useCallback(
    (patch: ComparisonPatch) =>
      setParams((prev) => {
        const next = new URLSearchParams(prev)
        next.set("tab", "comparison")
        if (patch.grain !== undefined) {
          // A month key is not a year key: a new range ends at its own default.
          if (patch.grain !== prev.get("cmp_grain")) next.delete("cmp_to")
          next.set("cmp_grain", patch.grain)
        }
        if (patch.to !== undefined) {
          if (patch.to) next.set("cmp_to", patch.to)
          else next.delete("cmp_to")
        }
        if (patch.memberIds !== undefined) {
          if (patch.memberIds.length)
            next.set("cmp_members", patch.memberIds.join(","))
          else next.delete("cmp_members")
        }
        if (patch.metric !== undefined) next.set("cmp_metric", patch.metric)
        return next
      }),
    [setParams]
  )

  return { query, metric, update }
}
